import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { AlertCircle, Check, Copy, Loader2, X } from 'lucide-react';
import {
  createInvite,
  fetchInvites,
  fetchLinkBookability,
  type CandidateInvite,
  type InterviewLink,
} from '../../services/interviewLinksApi';
import { BookabilitySummary } from './BookabilitySummary';

const dtFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Berlin',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Issue a personal booking link for one candidate, and show who already has one.
 *
 * Every candidate needs their own link: bookings are recorded per token, so a
 * shared URL would tell the second candidate it had "already been used". The
 * Lever candidate URL is optional but wanted — it is what lets the booking write
 * the interview back into Lever.
 */
export function InviteDialog({ link, onClose }: { link: InterviewLink; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [leverUrl, setLeverUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<CandidateInvite | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const { data: invites, refetch } = useQuery({
    queryKey: ['interview-link-invites', link.id],
    queryFn: () => fetchInvites(link.id),
  });

  // Advisory pre-send check: would a candidate opening this link see any slots?
  // fetchLinkBookability returns null on failure, and the summary hides itself.
  const { data: bookability, refetch: refetchBookability } = useQuery({
    queryKey: ['interview-link-bookability', link.id],
    queryFn: () => fetchLinkBookability(link.id),
    retry: false,
  });

  const emailValid = /^[^\s@,;]+@[^\s@,;.]+\.[^\s@,;]+$/.test(email.trim());
  const canSubmit = name.trim().length > 0 && emailValid;

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const invite = await createInvite(link.id, {
        candidateName: name.trim(),
        candidateEmail: email.trim(),
        opportunityId: leverUrl.trim() || undefined,
      });
      setJustCreated(invite);
      setName('');
      setEmail('');
      setLeverUrl('');
      await refetch();
      // Re-check before the next candidate in the same sitting: minting several
      // links in a row off one stale verdict is exactly how a recruiter would
      // send three people to the same empty booking page.
      void refetchBookability();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the candidate link');
    } finally {
      setSaving(false);
    }
  }

  async function copy(invite: CandidateInvite) {
    await navigator.clipboard.writeText(invite.bookingUrl);
    setCopied(invite.token);
    window.setTimeout(() => setCopied(null), 1500);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-16"
      // Escape closes it and a click on the backdrop dismisses it — without these
      // the only way out was the mouse on one small ✕.
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Candidate links for ${link.stageName}`}
        className="w-full max-w-xl rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Candidate links</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {link.stageName} · {link.jobTitle || link.name} · {link.minutes} min
            </p>
            <BookabilitySummary bookability={bookability} />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          className="mt-4 grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !saving) void submit();
          }}
        >
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">
              Candidate name <span className="text-red-600">*</span>
            </span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alexandra Meier"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">
              Candidate email <span className="text-red-600">*</span>
            </span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alexandra.meier@example.com"
            />
            {/* A disabled button with no reason is the bug this prevents: the
                surname ends up here and nothing explains the refusal. */}
            {email.trim() !== '' && !emailValid ? (
              <span className="mt-1 block text-xs text-red-600">
                That doesn’t look like an email address — the full name goes in the field on the
                left.
              </span>
            ) : (
              <span className="mt-1 block text-xs text-gray-500">
                The booking link and confirmation go to this address.
              </span>
            )}
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-xs font-medium text-gray-700">Lever candidate URL</span>
            <Input
              value={leverUrl}
              onChange={(e) => setLeverUrl(e.target.value)}
              placeholder="https://hire.lever.co/candidates/8a4646a1-…"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Paste the whole URL. Needed for the interview to appear in Lever.
            </span>
          </label>

          {error ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-800 sm:col-span-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="sm:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" size="sm" disabled={!canSubmit || saving}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                Create link
              </Button>
              {!canSubmit ? (
                <span className="text-xs text-gray-500">
                  {name.trim() === ''
                    ? 'Enter the candidate’s name.'
                    : 'Enter a valid email address.'}
                </span>
              ) : null}
            </div>
          </div>
        </form>

        {justCreated ? (
          <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-medium text-green-900">
              Link for {justCreated.candidateName} — send this to them:
            </p>
            <code className="mt-1.5 block break-all rounded bg-white/70 p-2 text-xs text-gray-800">
              {justCreated.bookingUrl}
            </code>
            <Button size="sm" className="mt-2" onClick={() => void copy(justCreated)}>
              {copied === justCreated.token ? (
                <Check className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Copy className="mr-1.5 h-3.5 w-3.5" />
              )}
              {copied === justCreated.token ? 'Copied' : 'Copy link'}
            </Button>
            {!justCreated.opportunityId ? (
              <p className="mt-2 text-xs text-[#854F0B]">
                No Lever candidate given — this booking will not create a Lever interview.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-5 border-t border-gray-200 pt-3">
          <h3 className="text-xs font-semibold text-gray-900">
            Already sent {invites?.length ? `(${invites.length})` : ''}
          </h3>
          {!invites ? (
            <p className="mt-2 text-xs text-gray-500">Loading…</p>
          ) : invites.length === 0 ? (
            <p className="mt-2 text-xs text-gray-500">Nobody has been sent this link yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100">
              {invites.map((i) => (
                <li key={i.token} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-gray-900">{i.candidateName}</span>
                    <span className="block truncate text-xs text-gray-500">{i.candidateEmail}</span>
                  </span>
                  {/* The question a recruiter actually has: who hasn't booked yet. */}
                  {i.booking ? (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">
                      Booked {dtFmt.format(new Date(i.booking.start))}
                    </span>
                  ) : i.cancelledAt ? (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
                      Cancelled
                    </span>
                  ) : (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                      Not booked yet
                    </span>
                  )}
                  {i.cancelReason || i.rescheduleReason ? (
                    <span className="w-full text-xs italic text-gray-500">
                      „{i.cancelReason || i.rescheduleReason}"
                    </span>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => void copy(i)}>
                    {copied === i.token ? (
                      <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {copied === i.token ? 'Copied' : 'Copy'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
